import mori from 'mori';
import { territorySort } from '../filters/territorySortFilter';
import getSelectedColumnsRecursive from './getSelectedColumnsRecursive';
import { digColumnValue } from '../util/util';

export default function(data, metaData, options, baseColumn, childColumn) {
  const isGeoMulti = mori.getIn(
    data.config,
    ['globalRules', 'Geomulti'],
    false
  );

  const isMedicare = mori.getIn(
    data.config,
    ['globalRules', 'medicare'],
    false
  );

  const rootSelection = Object.keys(options.selectedRows || {})[0] || false;
  const validSelection = rootSelection !== 'nested' && (
    !rootSelection ||
    metaData.columns[rootSelection] ||
    metaData.columns.nested[rootSelection] || false
  );
  let selectedRows = validSelection ?
    (options.selectedRows || {}) : null;
  let parentColumns;
  if (isGeoMulti) {
    // Get Summary Values (for lookups...)
    const summaries = mori.reduce(
      (acc, sheet) => mori.conj(
        acc,
        mori.hashMap(
          mori.first(sheet),
          mori.reduce(
            (acc1, row) => mori.conj(
              acc1,
              mori.hashMap(
                mori.get(row, baseColumn),
                mori.get(row, childColumn)
              )
            ),
            mori.hashMap(),
            mori.remove(
              (row) => mori.every(
                (item) => (mori.first(mori.rest(item)) || '')
                  .toString()
                  .indexOf('(All)') < 0,
                row
              ),
              mori.first(mori.rest(sheet))
            )
          )
        )
      ),
      mori.hashMap(),
      data.immutableData
    );

    parentColumns = mori.into(
      mori.vector(),
      mori.mapcat(
        (group) => mori.mapcat(
          (row) => {
            const selectAll =
              mori.first(mori.first(mori.last(row))) === 'all';
            const nestedObject = ((data.columns[mori.first(group)]) ?
              metaData.columns[mori.first(group)][mori.first(row)] :
              metaData.columns.nested[mori.first(group)]
                .children[mori.first(row)]) ||
              {};

            const base = mori.hashMap(
              baseColumn,
              nestedObject.label
            );
            return mori.isEmpty(mori.last(row)) || selectAll ?
              mori.vector(mori.vector(base)) :
              mori.map(
                (child) => {
                  // summary rows gets a rewrite
                  if (mori.first(child) === 'summary') {
                    return mori.set([
                      mori.hashMap(
                        baseColumn,
                        `${nestedObject.label} (All)`
                      ),
                      mori.hashMap(
                        childColumn,
                        mori.getIn(summaries, [
                          data.timeframes[options.currentTimeframe],
                          `${nestedObject.label} (All)`,
                        ], null)
                      ),
                    ]);
                  }
                  return mori.set([
                    base,
                    mori.hashMap(
                      childColumn,
                      nestedObject.children[mori.first(child)].label
                    ),
                  ]);
                },
                mori.last(row)
              );
          },
          // do territory sort here
          mori.sort(
            (a, b) => territorySort(
              mori.first(a),
              mori.first(b)
            ),
            mori.filter(
              (rowObj) => mori.first(rowObj) !== 'all',
              mori.last(group)
            )
          )
        ),
        mori.toClj(
          Object.keys((selectedRows || {})).reduce((selected, group) =>
            angular.extend({}, selected, {
              [group]: Object.keys(selectedRows[group] || {})
                .reduce((acc, column) =>
                  angular.extend({}, acc, {
                    [column]: Object.keys(
                      selectedRows[group][column] || {})
                        .reduce((acc1, child) => angular.extend({}, acc1, {
                          [child]: {},
                        }), {}),
                  }), {}),
            }), {})
        )
      )
    );
  } else {
    // Medicare has a special default state... US wrapup & States
    if (isMedicare && Object.keys(selectedRows).length === 0) {
      const hasStates = !!metaData.columns.nested.states;
      const hasWrapups = !!metaData.columns.wrapups;
      if (hasStates && hasWrapups) {
        selectedRows = {
          'states': { 'all': {} },
          'wrapups': { 'united-states': {} },
        };
      }
    }
    // Make an array of selected/parent selection rows
    parentColumns = mori.into(
      mori.vector(),
      mori.mapcat(
        (group) => mori.mapcat(
          (row) => getSelectedColumnsRecursive(
            data,
            metaData,
            baseColumn,
            childColumn,
            row,
            group
          ),
          // do territory sort here
          mori.sort(
            (a, b) => territorySort(
              mori.first(a),
              mori.first(b)
            ),
            mori.filter(
              (rowObj) => mori.first(rowObj) !== 'all',
              mori.last(group)
            )
          )
        ),
        mori.toClj(
          Object.keys((selectedRows || {})).reduce((selected, group) =>
            angular.extend({}, selected, {
              [group]: getSelectedColumns(selectedRows, group),
            }),
          {})
        )
      )
    );
  }

  const wrapupColumns = mori.set(mori.mapcat(
    (val) => mori.set([mori.first(val)]),
    mori.filter(
      (row) => mori.get(mori.first(mori.rest(row)), 'selected'),
      mori.toClj(metaData.columns.wrapups)
    )
  ));

  /**
   * Digs into selected columns to produce required data for getSelected
   * to continue processing, it has a req of the data.columns that
   * can't be passed in via attr since recursion is a pain...
   * @param {object} Object to dig into
   * @param {string} Initial key to dig
   * @param {...string} parent keys
   * @return {object} Object of all selectedColumns
   */
  function getSelectedColumns() {
    const args = Array.prototype.slice.call(arguments);
    const rows = args[0];
    const key = args[1];
    const keys = args.slice(1, args.length);

    if (rows[key].all) {
      // data.columns needs to be curried into from above
      return Object.keys(digColumnValue(data.columns, ...keys.reverse()) || {})
        .reduce(
          (acc, column) => angular.extend(
            {},
            acc,
            {
              [column]: {},
            }
          ),
          {}
        );
    } else if (Object.keys(rows[key]).length) {
      return Object.keys(rows[key]).reduce(
        (selected, nested) => angular.extend(
          {},
          selected,
          {
            [nested]: getSelectedColumns(rows[key], nested, ...keys),
          }
        ),
        {}
      );
    }
    // If all else doesn't matter, just return the rows/key
    return rows[key]; // This SHOULD have a label...
  }

  return mori.into(parentColumns, wrapupColumns);
}



// WEBPACK FOOTER //
// ../datacenter/app/js/services/getSelectedColumns.js